import SentryService from '@/services/sentry/Sentry.service';

interface TokenFetcher {
  (): Promise<string>;
}

interface TokenManagerState {
  lastRequestTime: number;
  failureCount: number;
  pendingRequest: Promise<string> | null;
}

/**
 * Creates a token request function with exponential backoff to prevent rate limit cascades
 * Backoff progression after failures: 0 failures = 0s, 1 failure = 1s, 2 failures = 2s, 3 = 4s, 4 = 8s... up to 30s max
 * Deduplicates simultaneous requests to prevent multiple backend calls
 */
export function createTokenRequestWithBackoff(
  fetchToken: TokenFetcher,
  state: TokenManagerState,
) {
  return async (
    resolve: (token: string) => void,
    reject: (error: Error) => void,
  ) => {
    // If there's already a pending request, reuse it instead of creating a new one
    // Deduped callers just await and propagate - state is managed by the original caller
    if (state.pendingRequest) {
      SentryService.captureInfo(
        'Sendbird token request deduplicated - reusing pending request',
      );
      try {
        const token = await state.pendingRequest;
        resolve(token);
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
      return;
    }

    // Implement exponential backoff to prevent 429 cascades
    // Only apply delay if there have been previous failures
    const now = Date.now();
    const timeSinceLastRequest = now - state.lastRequestTime;
    const minDelay =
      state.failureCount > 0
        ? Math.min(1000 * Math.pow(2, state.failureCount - 1), 30000) // 1s, 2s, 4s, 8s... up to 30s
        : 0; // No delay when no failures

    if (minDelay > 0 && timeSinceLastRequest < minDelay) {
      const waitTime = minDelay - timeSinceLastRequest;
      await new Promise((r) => setTimeout(r, waitTime));
    }

    state.lastRequestTime = Date.now();

    // Create the request with state management attached to the promise itself
    // This ensures state is updated exactly once regardless of how many callers await it
    const requestPromise = fetchToken()
      .then((token) => {
        // Success: clear pending request and reset failure count
        state.pendingRequest = null;
        state.failureCount = 0;
        return token;
      })
      .catch((error) => {
        // Failure: clear pending request and increment failure count ONCE
        state.pendingRequest = null;
        state.failureCount += 1;

        SentryService.captureError(
          'Failed to fetch Sendbird session token',
          error,
          {
            failureCount: state.failureCount,
          },
        );
        throw error;
      });

    state.pendingRequest = requestPromise;

    // Await and propagate the result
    try {
      const token = await requestPromise;
      resolve(token);
    } catch (error) {
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  };
}

/**
 * Creates a new token manager state object
 * Use this to maintain separate state for different token request contexts
 */
export function createTokenManagerState(): TokenManagerState {
  return {
    lastRequestTime: 0,
    failureCount: 0,
    pendingRequest: null,
  };
}
